import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogState,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { ErrorService } from '../error.service';
import { HateoasError, isHateoasError } from './hateoas-error';

@Component({
  selector: 'app-error-modal',
  templateUrl: './error-modal.component.html',
  styleUrls: ['./error-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDialogContent,
    MatDialogActions,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ErrorModalComponent {
  title = "Une erreur s'est produite";
  titleMatIconLigText = 'error_outline';
  codes: unknown[] = [];

  public errors: unknown[] = [];
  public errorDescription?: string = undefined;

  private errorsSubscription: Subscription;
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(MatDialogRef<string>);
  private errorService = inject(ErrorService);
  private location = inject(Location);

  constructor() {
    this.errorsSubscription = this.errorService.errors$.subscribe((errors) => {
      this.errors = Array.from(errors);
      this.init();
      this.cdr.markForCheck();
    });
  }

  get description(): string {
    return this.errorDescription || '';
  }

  init() {
    if (this.errors?.length) {
      const error = this.errors[0];
      if (typeof error === 'object' && error !== null) {
        const typedError = error as { message?: unknown; title?: unknown };

        if (typeof typedError.message === 'string') {
          this.errorDescription = typedError.message;
          this.titleMatIconLigText = 'error_outline';
        } else if (typeof typedError.title === 'string') {
          this.errorDescription = typedError.title;
          this.titleMatIconLigText = 'error_outline';
        } else if (typedError.message) {
          this.codes.push(...JSON.stringify(typedError.message, null, 2).split('\n'));
        }
      }

      if (isHateoasError(error)) {
        const hateoasError = this.errors[0] as HateoasError;

        const errorMessages: string[] = [];
        const embeddedErrors = hateoasError._embedded?.errors;
        const directErrors = hateoasError.errors;

        const rawErrors = directErrors?.length ? directErrors : embeddedErrors;
        if (rawErrors?.length) {
          rawErrors
            .map((err) => {
              const field = (err as { field?: string }).field;
              const message = (err as { message?: string }).message;
              if (typeof message === 'string' && message.length > 0) {
                return field ? `${field}: ${message}` : message;
              }
              return undefined;
            })
            .filter((msg): msg is string => typeof msg === 'string' && msg.length > 0)
            .forEach((msg) => errorMessages.push(msg));
        }

        if (!rawErrors?.length && hateoasError.message) {
          errorMessages.push(hateoasError.message);
        }

        switch (hateoasError.message || hateoasError.title) {
          case 'Internal Server Error':
            this.title = 'Erreur du serveur';
            this.titleMatIconLigText = 'error_outline';
            this.errorDescription =
              'Une erreur interne est survenue sur le serveur. Veuillez réessayer plus tard.';
            break;
          case 'Not Found': {
            this.title = 'Ressource introuvable';
            this.titleMatIconLigText = 'search';
            this.errorDescription =
              (hateoasError.errors?.[0] as { message: string } | undefined)?.message ||
              (hateoasError._embedded?.errors?.[0] as { message: string } | undefined)?.message ||
              'La ressource demandée est introuvable.';
            errorMessages.length = 0;
            const links = hateoasError._links as Record<string, { href?: string }> | undefined;
            const href = links?.['self']?.href;
            if (href) {
              errorMessages.push('404: ' + href);
            }
            break;
          }
          case 'Unauthorized':
            this.title = 'Non autorisé';
            this.titleMatIconLigText = 'account_box';
            this.errorDescription =
              "Vous n'êtes pas autorisé à effectuer cette action, vous pouvez essayer de vous reconnecter.";
            break;
          case 'Forbidden':
            this.title = 'Accès refusé';
            this.titleMatIconLigText = 'security';
            this.errorDescription = "Vous n'avez pas les droits pour effectuer cette action.";
            break;
          case 'Bad Request':
            this.title = 'Requête invalide';
            this.titleMatIconLigText = 'assignment';
            this.errorDescription = 'Le serveur à rejeté votre requête:';
            break;
          default:
            this.title = "Une erreur s'est produite";
            this.titleMatIconLigText = 'error_outline';
            this.errorDescription = hateoasError.message || hateoasError.title;
            break;
        }
        this.codes.push(...errorMessages);
      }
    }
  }

  public close() {
    this.dialogRef.close();
    this.errorService.isModalOpen = false;
    this.errorsSubscription.unsubscribe();
    this.errorService.clear();
    // Sometime, there is errors like "Can't find column with id" from a `MatTable` that prevent the error modal to close
    // A workaround is to check after a delay if the modal is still closing and if so, we change the location to the previous one
    setTimeout(() => {
      if (this.dialogRef.getState() == MatDialogState.CLOSING) {
        this.errorService.isModalOpen = false;
        this.location.back();
      }
    }, 500);
    this.codes = [];
  }
}
